import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Output } from '@angular/core';
import { ActionIconDef, GridActionIconClickEvent, GridConfig, GridHyperlinkClickEvent, GridRow } from '@inavista/components/grid';
import { APP_CONFIG, AppConfig } from 'projects/shared/src/lib/app-config.token';
import { UserRoles } from '../../../../../shared/constants/app.constants';
import { CommonService } from '../../../../../shared/services/common.service';

// General section → Add address: every address (dbo.vwUnusedAddresses), on @inavista/components. Rows: RIMS Master
// api/grid/user-profile-search/addresses (paged in SQL; before, odata/Address, which needed no sign-in). Picking a row
// (the arrow, or its Address Code) hands it to the General section, as before. Excel for every role but Sales Rep.
// Fixed: the old Email column read `eMail`, a field the rows never had, so it was always empty.
const COLUMNS = ['addressCode', 'name', 'address', 'email', 'telephone'];
const SELECT_ICON = '<line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />';

@Component({
    selector: 'app-addresses-grid-popup',
    templateUrl: './addresses-grid-popup.component.html',
    styleUrls: ['./addresses-grid-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddressesGridPopupComponent {
  @Output() onClose = new EventEmitter();
  isExpanded = false;
  readonly gridConfig: GridConfig;
  readonly actionIcons: ActionIconDef[] = [{ key: 'select', label: 'Add this address', icon: SELECT_ICON, colorFn: () => '#000000de' }];

  constructor(@Inject(APP_CONFIG) config: AppConfig, commonService: CommonService) {
    this.gridConfig = {
      apiBaseUrl: `${config.masterApiURL}grid/user-profile-search/addresses`,
      keyColumn: 'id',
      clientSide: false,
      pageSize: 200,
      rowHeight: 26,
      loadingIndicator: 'banner',
      stretchColumns: true,
      wrapHeaders: true,
      enableSearch: true,
      enableContextMenuFilter: true,
      enableFitColumns: true,
      enableResetLayout: true,
      enableExport: commonService.getUSER()?.role !== UserRoles.salesRep,
      defaultColumnOrder: COLUMNS,
      // The other address fields stay in the column picker; the row identity never shows.
      startHiddenColumns: ['name2', 'address2', 'houseNumber', 'postBoxNumber', 'city', 'fax', 'country', 'route', 'timeZone',
        'stateProvince', 'zipCode', 'latitude', 'longitude'],
      hiddenByDefaultColumns: ['id'],
      columnLabels: { addressCode: 'Address Code', name: 'Name', address: 'Address Description', email: 'Email', telephone: 'Telephone' },
      columnWidths: Object.fromEntries(COLUMNS.map(c => [c, 120])),
      columnAlign: Object.fromEntries(COLUMNS.map(c => [c, {
        header: { horizontal: 'left' as const },
        ...(c === 'addressCode' ? { horizontal: 'center' as const } : {}),
      }])),
      // As before: the address followed by its city, state, zip code and country.
      columnFormatters: {
        address: (_value, row) => [row['address'], row['city'], row['stateProvince'], row['zipCode'], row['country']]
          .filter(v => v !== null && v !== undefined && v !== '').join(', '),
      },
      hyperlinkColumns: ['addressCode'],
      emptyStateMessage: 'No addresses to show.',
      noResultsMessage: 'No address matches. Clear the search or the filters to see every address.',
      persistenceKey: 'rims.users.address-search',
      persistenceVersion: 1,
    };
  }

  private pick(row: GridRow) {
    this.onClose.emit(row);
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey === 'select') this.pick(event.row);
  }

  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    this.pick(event.row);
  }

  close() {
    this.onClose.emit(true);
  }

  expandCollapse() {
    this.isExpanded = !this.isExpanded;
  }
}
